import * as PIXI from "pixi.js";

class WebGLFireball {
  constructor(app) {
    this.app = app;
    this.container = new PIXI.Container();
    this.fireballs = [];
    this.trails = [];
    this.time = 0;
    this.init();
  }

  init() {
    // Create one fireball per effect instance
    this.createFireball();

    this.updateBound = () => this.update();
    this.app.ticker.add(this.updateBound);
  }

  createFireball() {
    const fireball = new PIXI.Graphics();
    fireball.circle(0, 0, 12);

    // Create gradient effect with multiple circles
    const gradient = new PIXI.Graphics();
    gradient.circle(0, 0, 12);
    gradient.fill(0xff4500); // Orange red core

    const outerGlow = new PIXI.Graphics();
    outerGlow.circle(0, 0, 16);
    outerGlow.fill(0xff6500);
    outerGlow.alpha = 0.7;

    const innerCore = new PIXI.Graphics();
    innerCore.circle(0, 0, 8);
    innerCore.fill(0xffff00); // Yellow core

    fireball.addChild(outerGlow);
    fireball.addChild(gradient);
    fireball.addChild(innerCore);

    // Physics properties - ping pong ball style with variation
    fireball.x = Math.random() * this.app.screen.width;
    fireball.y = Math.random() * this.app.screen.height;

    // Ensure decent starting velocity with minimum speed
    const minSpeed = 4;
    const maxSpeed = 8;
    const angle = Math.random() * Math.PI * 2; // Random direction
    const speed = minSpeed + Math.random() * (maxSpeed - minSpeed); // Speed between 4-8
    fireball.vx = Math.cos(angle) * speed;
    fireball.vy = Math.sin(angle) * speed;
    fireball.bounce = 0.95; // Slight energy loss for realism

    // Trail system with variation
    fireball.trail = [];
    fireball.maxTrailLength = 10 + Math.random() * 8;

    // Individual fireball properties for visual variety
    fireball.pulsePhase = Math.random() * Math.PI * 2;
    fireball.pulseSpeed = Math.random() * 0.08 + 0.04;

    this.fireballs.push(fireball);
    this.container.addChild(fireball);
  }

  update() {
    this.time += 0.016;

    this.fireballs.forEach((fireball) => {
      // Store previous position for trail
      fireball.trail.push({
        x: fireball.x,
        y: fireball.y,
        life: 1.0,
      });

      // Limit trail length
      if (fireball.trail.length > fireball.maxTrailLength) {
        fireball.trail.shift();
      }

      // Apply physics - no gravity, just constant velocity
      fireball.x += fireball.vx;
      fireball.y += fireball.vy;

      // Bounce off walls
      if (fireball.x <= 12 || fireball.x >= this.app.screen.width - 12) {
        fireball.vx *= -fireball.bounce;
        fireball.x = Math.max(
          12,
          Math.min(this.app.screen.width - 12, fireball.x)
        );
      }

      if (fireball.y <= 12 || fireball.y >= this.app.screen.height - 12) {
        fireball.vy *= -fireball.bounce;
        fireball.y = Math.max(
          12,
          Math.min(this.app.screen.height - 12, fireball.y)
        );
      }

      // Individual pulsing effect for each fireball
      fireball.pulsePhase += fireball.pulseSpeed;
      const pulse = Math.sin(fireball.pulsePhase) * 0.15 + 1;
      fireball.scale.set(pulse);

      // Update trail life
      fireball.trail.forEach((point, index) => {
        point.life = (index + 1) / fireball.trail.length;
      });
    });

    // Render trails
    this.renderTrails();
  }

  renderTrails() {
    // Clear previous trails
    this.trails.forEach((trail) => {
      if (trail.parent) {
        trail.parent.removeChild(trail);
      }
    });
    this.trails = [];

    this.fireballs.forEach((fireball) => {
      fireball.trail.forEach((point, index) => {
        if (index === 0) return; // Skip first point

        const trailSegment = new PIXI.Graphics();
        const size = point.life * 8;
        const alpha = point.life * 0.6;

        trailSegment.circle(0, 0, size);
        trailSegment.fill(0xff6500);
        trailSegment.alpha = alpha;
        trailSegment.x = point.x;
        trailSegment.y = point.y;

        this.container.addChild(trailSegment);
        this.trails.push(trailSegment);
      });
    });
  }

  destroy() {
    try {
      if (this.updateBound && this.app && this.app.ticker) {
        this.app.ticker.remove(this.updateBound);
        this.updateBound = null;
      }

      this.trails.forEach((trail) => {
        if (trail.parent) {
          trail.parent.removeChild(trail);
        }
      });
      this.trails = [];
      this.fireballs = [];

      if (this.container) {
        this.container.destroy({ children: true, texture: true });
        this.container = null;
      }
    } catch (e) {
      console.warn("Error destroying WebGLFireball:", e);
    }
  }
}

export default WebGLFireball;
